import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import type { Session } from "@supabase/supabase-js";
import { createClient } from "@supabase/supabase-js";
import { Eye, EyeOff, LockKeyhole } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getErrorMessage, supabase } from "../lib/supabase";

/**
 * Cliente de activación aislado: nunca importa la sesión ya abierta en KALITE
 * y no guarda la sesión temporal del invitado en localStorage.
 */
const invitationClient = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      flowType: "implicit",
    },
  },
);

function invitationError(caught: unknown): string {
  const message = getErrorMessage(caught);
  if (/otp.expired|expired|invalid.*(token|link|grant)|already.used|token.has.been.used/i.test(message)) {
    return "Este enlace de invitación ya fue utilizado, no es válido o expiró. Solicita una nueva invitación.";
  }
  return message;
}

async function redeemInvitation(): Promise<Session> {
  const url = new URL(window.location.href);
  const query = url.searchParams;
  const hash = new URLSearchParams(url.hash.replace(/^#/, ""));
  const remoteError = query.get("error_description") || hash.get("error_description");
  if (remoteError) throw new Error(remoteError.replace(/\+/g, " "));

  // Nunca aceptar una sesión existente como prueba de invitación.
  // La prueba debe proceder exclusivamente del token/código de ESTA URL.
  const tokenHash = query.get("token_hash");
  let session: Session | null = null;

  if (tokenHash && query.get("type") === "invite") {
    // TokenHash se consume de forma atómica por Supabase Auth (uso único).
    const { data, error } = await invitationClient.auth.verifyOtp({
      token_hash: tokenHash,
      type: "invite",
    });
    if (error) throw error;
    session = data.session;
  } else if (hash.get("type") === "invite" &&
             hash.get("access_token") && hash.get("refresh_token")) {
    // Compatibilidad con el enlace estándar {{ .ConfirmationURL }}.
    // Supabase Auth ya consumió el token de invitación antes de redirigir aquí.
    const { data, error } = await invitationClient.auth.setSession({
      access_token: hash.get("access_token")!,
      refresh_token: hash.get("refresh_token")!,
    });
    if (error) throw error;
    session = data.session;
  } else if (query.get("code") && query.get("type") === "invite") {
    const { data, error } = await invitationClient.auth.exchangeCodeForSession(query.get("code")!);
    if (error) throw error;
    session = data.session;
  } else {
    throw new Error("El enlace no contiene una invitación válida. Abre el enlace original recibido por correo.");
  }

  if (!session?.user?.id || !session.user.email) {
    throw new Error("No fue posible comprobar la identidad del usuario invitado.");
  }

  // No conservar credenciales/tokens en la URL o el historial del navegador.
  window.history.replaceState(window.history.state, "", "/activar-cuenta");
  return session;
}

export function InviteActivationPage() {
  const navigate = useNavigate();
  const verification = useRef<Promise<Session> | null>(null);
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    // React.StrictMode monta efectos dos veces en desarrollo.
    // Una única promesa impide consumir el token OTP en dos verificaciones.
    if (!verification.current) verification.current = redeemInvitation();
    verification.current.then(session => {
      if (!mounted) return;
      setEmail(session.user.email ?? "");
      setReady(true);
    }).catch(caught => {
      if (mounted) setError(invitationError(caught));
    }).finally(() => {
      if (mounted) setChecking(false);
    });
    return () => { mounted = false; };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy) return;
    setError("");
    if (password.length < 8) {
      setError("La contraseña debe tener al menos ocho caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setBusy(true);
    try {
      // Nunca actualizar la contraseña de una sesión previa de otro usuario.
      const { error: updateError } = await invitationClient.auth.updateUser({ password });
      if (updateError) throw updateError;

      // Invalidar la sesión temporal del invitado (incluidos tokens de retorno).
      const { error: temporarySignOutError } = await invitationClient.auth.signOut();
      if (temporarySignOutError) throw temporarySignOutError;

      // Si este dispositivo tenía otra cuenta de KALITE abierta, terminar solo
      // su sesión LOCAL para que el login no redirija a la cuenta anterior.
      const { error: localSignOutError } = await supabase.auth.signOut({ scope: "local" });
      if (localSignOutError) throw localSignOutError;

      setReady(false);
      navigate("/acceso?cuenta=activada", { replace: true });
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return <main className="auth-page">
    <section className="auth-side">
      <div>
        <span className="eyebrow light">CONTROL TÉCNICO DE OBRA</span>
        <h1>Calidad verificable.<br />Decisiones con evidencia.</h1>
        <p>Centraliza inspecciones, pruebas, certificados y comunicaciones contractuales en un solo entorno.</p>
      </div>
      <small>KALITE · Plataforma de gestión de calidad</small>
    </section>
    <section className="auth-panel">
      <form className="auth-card" onSubmit={event => void submit(event)}>
        <span className="eyebrow">INVITACIÓN A KALITE</span>
        <h2>Activa tu cuenta</h2>
        <p>Define tu contraseña para utilizar KALITE. Después ingresarás desde la página de acceso.</p>
        {checking && <div className="notice info">Verificando tu invitación…</div>}
        {error && <div className="notice error" role="alert">{error}</div>}
        {ready && <>
          <label>Correo de la invitación<input value={email} readOnly aria-readonly="true" /></label>
          <label>Nueva contraseña
            <div className="input-icon"><LockKeyhole size={18}/>
              <input required minLength={8} autoComplete="new-password"
                type={showPassword ? "text" : "password"} value={password}
                onChange={event => setPassword(event.target.value)}/>
              <button type="button" aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                onClick={() => setShowPassword(previous => !previous)}>
                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </label>
          <label>Confirmar contraseña
            <input required minLength={8} type={showPassword ? "text" : "password"}
              autoComplete="new-password" value={confirm}
              onChange={event => setConfirm(event.target.value)}/>
          </label>
          <button type="submit" className="button primary wide" disabled={busy}>
            {busy ? "Activando cuenta…" : "Establecer contraseña"}
          </button>
        </>}
        <Link className="auth-link" to="/acceso">Ir al inicio de sesión</Link>
      </form>
    </section>
  </main>;
}
